import axios from "axios";

export interface publicationDateResult {
  expired?: boolean,
  value?: string,
}

function makePublicationDateChecker(url: string, dateKey: string): Function {
  return () => publicationDateChecker(url, dateKey);
}

async function publicationDateChecker(
  url: string,
  dateKey: string
): Promise<publicationDateResult> {
  let localDate = localStorage.getItem(dateKey);
  return await axios({ method: "get", url }).then(resp => {
    if (!resp.data) console.error("Check error : no data");
    let pubDate = resp.data.result;
    let expired = !localDate || localDate == "undefined" || localDate < pubDate;
    return {
      'expired': expired,
      'value': pubDate,
    };
  });
}

/**
 * Clear expired localStorage cache if necessary
 */
async function clearExpiredCacheStorage(gtfsUri, encodeUriPath, network) : Promise<publicationDateResult> {
  let cacheKey = `${gtfsUri}-${network}-publicationDate`;
  let urlCheck = "/publicationdate?reseau=" + network;
  if (encodeUriPath) {
    urlCheck = encodeURIComponent(urlCheck);
  }
  urlCheck = gtfsUri + urlCheck;
  let expirationChecker = makePublicationDateChecker(
    urlCheck,
    cacheKey,
  );
  let expirationResult = await expirationChecker();
  if (expirationResult.expired) {
    localStorage.clear();
    localStorage.setItem(cacheKey, expirationResult.value);
  }
  return expirationResult;
}

/**
 * Call a WS using the getValue function
 * @param key the cache key
 * @param getValue a function to get the value
 * @returns the value returned by the function or stored in the cache
 */
function localStorageProxy(
  key: string, // localStorage key used to set/get the value
  getValue: Function, // async function returning the live value
): Function {
  return async function<T>(value: T | null): Promise<T | null> {
    // Try to get the value in localStorage
    let fetched = localStorage.getItem(key);
    if (fetched) {
      value = JSON.parse(fetched);
    } else {
      // If we don't find a value in localStorage, get the value and store the result in localStorage
      value = await getValue();
      localStorage.setItem(key, JSON.stringify(value));
    }
    return value;
  };
}

/*
function localStorageProxy(
  key: string, // localStorage key used to set/get the value
  getValue: Function, // async function returning the live value
  expirationChecker: Function // async function returning if the value need to be updated from live provider
): Function {
  return async function<T>(value: T | null): Promise<T | null> {
    let [needRefresh, updateExpirationDate] = await expirationChecker();
    if (!needRefresh) {
      let fetched = localStorage.getItem(key);
      if (fetched) {
        value = JSON.parse(fetched);
      }
    }

    if (needRefresh || !value) {
      value = await getValue();
      localStorage.setItem(key, JSON.stringify(value));
      updateExpirationDate();
    }

    return value;
  };
}*/

/**
 * Call a WS and cache the result during n seconds
 * 
 * @param key the cache key
 * @param getValue a function to get the value
 * @param cacheDuration the cache duration, in seconds
 * @return the value returned by the getValue function or stored in the cache
 */
function callWsCached(
  key: string, // localStorage key used to set/get the value
  getValue: Function, // async function returning the live value
  cacheDuration: number = 3600 // Duration of cache, in seconds
 ): Function {
  return async function<T>(value: T | null): Promise<T | null> {
    let needRefresh = true;
    let dateKey = `${key}-dateExpiration`;
    let expirationValue = parseInt(localStorage.getItem(dateKey));

    if (expirationValue) {
      let date = new Date();

      if (date.getTime().valueOf() < expirationValue) {
        value = JSON.parse(localStorage.getItem(key));
        needRefresh = false;
      }
    }
    if (needRefresh || !value) {
      value = await getValue();
      let date = new Date();
      date.setSeconds(date.getSeconds() + cacheDuration);
      let expirationTimestamp  = date.getTime().valueOf();
      localStorage.setItem(dateKey, expirationTimestamp.toString());
      localStorage.setItem(key, JSON.stringify(value));
    }

    return value;
  };
}

// Scroll in overflow:hidden divs (ie in select autocomplete suggestions)
function scrollParentToChild(
  parent: HTMLElement,
  child: HTMLElement,
  stickBottom = true
) {
  var parentRect = parent.getBoundingClientRect();
  var parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth
  };

  var childRect = child.getBoundingClientRect();
  var isViewable =
    childRect.top >= parentRect.top &&
    childRect.top <= parentRect.top + parentViewableArea.height;
  if (!isViewable) {
    parent.scrollTop = childRect.top + parent.scrollTop - parentRect.top;
    if (stickBottom) {
      parent.scrollTop =
        parent.scrollTop - (parentRect.height - childRect.height);
    }
  }
}

let defaultOptions = function (options) {
  if (typeof options === 'number') {
    // Legacy
    options = {
      precision: options
    };
  } else {
    options = options || {};
  }

  options.precision = options.precision || 5;
  options.factor = options.factor || Math.pow(10, options.precision);
  options.dimension = options.dimension || 2;
  return options;
};

let PolylineUtil = {
  encode: function (points, options) {
    options = defaultOptions(options);

    var flatPoints = [];
    for (var i = 0, len = points.length; i < len; ++i) {
      var point = points[i];

      if (options.dimension === 2) {
        flatPoints.push(point.lat || point[0]);
        flatPoints.push(point.lng || point[1]);
      } else {
        for (var dim = 0; dim < options.dimension; ++dim) {
          flatPoints.push(point[dim]);
        }
      }
    }

    return this.encodeDeltas(flatPoints, options);
  },

  decode: function (encoded, options) {
    let points = [];
    options = defaultOptions(options);
    let flatPoints = this.decodeDeltas(encoded, options);
    for (var i = 0, len = flatPoints.length; i + (options.dimension - 1) < len;) {
      let point = [];
      for (var dim = 0; dim < options.dimension; ++dim) {
        point.push(flatPoints[i++]);
      }
      points.push(point);
    }
    return points;
  },

  encodeDeltas: function (numbers, options) {
    options = defaultOptions(options);

    var lastNumbers = [];

    for (var i = 0, len = numbers.length; i < len;) {
      for (var d = 0; d < options.dimension; ++d, ++i) {
        var num = numbers[i];
        var delta = num - (lastNumbers[d] || 0);
        lastNumbers[d] = num;

        numbers[i] = delta;
      }
    }

    return this.encodeFloats(numbers, options);
  },

  decodeDeltas: function (encoded, options) {
    options = defaultOptions(options);
    var lastNumbers = [];
    var numbers = this.decodeFloats(encoded, options);
    for (var i = 0, len = numbers.length; i < len - 1;) {
      for (var d = 0; d < options.dimension; ++d, ++i) {
        let num = Math.round((lastNumbers[d] = numbers[i] + (lastNumbers[d] || 0)) * options.factor) / options.factor;
        numbers[i] = num;
      }
    }
    return numbers;
  },

  encodeFloats: function (numbers, options) {
    options = defaultOptions(options);

    for (var i = 0, len = numbers.length; i < len; ++i) {
      numbers[i] = Math.round(numbers[i] * options.factor);
    }

    return this.encodeSignedIntegers(numbers);
  },

  decodeFloats: function (encoded, options) {
    options = defaultOptions(options);

    var numbers = this.decodeSignedIntegers(encoded);
    for (var i = 0, len = numbers.length; i < len; ++i) {
      numbers[i] /= options.factor;
    }

    return numbers;
  },

  encodeSignedIntegers: function (numbers) {
    for (var i = 0, len = numbers.length; i < len; ++i) {
      var num = numbers[i];
      numbers[i] = (num < 0) ? ~(num << 1) : (num << 1);
    }

    return this.encodeUnsignedIntegers(numbers);
  },

  decodeSignedIntegers: function (encoded) {
    var numbers = this.decodeUnsignedIntegers(encoded);

    for (var i = 0, len = numbers.length; i < len; ++i) {
      var num = numbers[i];
      numbers[i] = (num & 1) ? ~(num >> 1) : (num >> 1);
    }

    return numbers;
  },

  encodeUnsignedIntegers: function (numbers) {
    var encoded = '';
    for (var i = 0, len = numbers.length; i < len; ++i) {
      encoded += this.encodeUnsignedInteger(numbers[i]);
    }
    return encoded;
  },

  decodeUnsignedIntegers: function (encoded) {
    var numbers = [];

    var current = 0;
    var shift = 0;

    for (var i = 0, len = encoded.length; i < len; ++i) {
      var b = encoded.charCodeAt(i) - 63;

      current |= (b & 0x1f) << shift;

      if (b < 0x20) {
        numbers.push(current);
        current = 0;
        shift = 0;
      } else {
        shift += 5;
      }
    }

    return numbers;
  },

  encodeSignedInteger: function (num) {
    num = (num < 0) ? ~(num << 1) : (num << 1);
    return this.encodeUnsignedInteger(num);
  },

  // This function is very similar to Google's, but I added
  // some stuff to deal with the double slash issue.
  encodeUnsignedInteger: function (num) {
    var value, encoded = '';
    while (num >= 0x20) {
      value = (0x20 | (num & 0x1f)) + 63;
      encoded += (String.fromCharCode(value));
      num >>= 5;
    }
    value = num + 63;
    encoded += (String.fromCharCode(value));

    return encoded;
  }
};


export { scrollParentToChild, localStorageProxy, clearExpiredCacheStorage, PolylineUtil, callWsCached };
